import { IBalusterGeometry, IRailingGeometry, IVec3 } from '@stair-craft/common/util';

import { POST_ABOVE_HANDRAIL } from '../../constants';

/** Sampling step along a handrail segment when looking for the part over a post (mm). */
const SAMPLE_STEP: number = 2;
/** Plan distances within this much count as on the post face (mm). */
const SLACK: number = 0.5;
/** How far from a post's top a handrail still counts as the one the post carries (mm). */
const HANDRAIL_REACH: number = 400;

/** A railing post with its half width: posts of different railings meet at a shared corner. */
interface ISizedPost {
    readonly post: IBalusterGeometry;
    readonly half: number;
}

/** Whether a plan point lies over a post's square, its faces included. */
function overPost(point: IVec3, sized: ISizedPost): boolean {
    const reach: number = sized.half + SLACK;
    return Math.abs(point.x - sized.post.position.x) <= reach && Math.abs(point.z - sized.post.position.z) <= reach;
}

/** The share of a plan step `delta` from `offset` off the post centre that reaches the post face; Infinity without a step. */
function shareToFace(offset: number, delta: number, half: number): number {
    return Math.abs(delta) > SLACK ? (half - Math.sign(delta) * offset) / Math.abs(delta) : Infinity;
}

/**
 * Moves a handrail end that lies over a post along its segment to the post face: the handrail butts
 * into the post instead of running into its middle. A vertical jog, or a segment that ends inside
 * the post, is left alone.
 */
function trimEnd(end: IVec3, next: IVec3, posts: readonly ISizedPost[]): void {
    const sized: ISizedPost | undefined = posts.find((candidate: ISizedPost) => overPost(end, candidate));
    if (!sized) {
        return;
    }
    const dx: number = next.x - end.x;
    const dz: number = next.z - end.z;
    const share: number = Math.min(
        shareToFace(end.x - sized.post.position.x, dx, sized.half),
        shareToFace(end.z - sized.post.position.z, dz, sized.half)
    );
    if (!Number.isFinite(share) || share >= 1) {
        return;
    }
    end.x += dx * share;
    end.y += (next.y - end.y) * share;
    end.z += dz * share;
}

/**
 * The highest handrail top over a post's square near the post's own top, or −Infinity where the
 * handrail does not pass there. A spiral turning past a full circle passes over the same post a
 * floor higher, and that pass is not the handrail the post carries.
 */
function handrailTopOver(railing: IRailingGeometry, sized: ISizedPost): number {
    const postTop: number = sized.post.position.y + sized.post.height;
    const points: IVec3[] = railing.handrail.points;
    let top: number = -Infinity;
    for (let i: number = 0; i < points.length - 1; i++) {
        const from: IVec3 = points[i];
        const to: IVec3 = points[i + 1];
        const plan: number = Math.hypot(to.x - from.x, to.z - from.z);
        // A bar cut square to its axis is `secant` times taller measured upright.
        const secant: number = plan > SLACK ? Math.hypot(plan, to.y - from.y) / plan : 1;
        const halfHeight: number = (railing.handrail.height / 2) * secant;
        const samples: number = Math.max(1, Math.ceil(plan / SAMPLE_STEP));
        for (let s: number = 0; s <= samples; s++) {
            const t: number = s / samples;
            const point: IVec3 = { x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t, z: from.z + (to.z - from.z) * t };
            if (overPost(point, sized) && Math.abs(point.y - postTop) <= HANDRAIL_REACH) {
                top = Math.max(top, point.y + halfHeight);
            }
        }
    }
    return top;
}

/** Whether a baluster stands in a post's place: their squares overlap in plan and their heights meet. */
function insidePost(baluster: IBalusterGeometry, half: number, sized: ISizedPost): boolean {
    const reach: number = half + sized.half - SLACK;
    const inPlan: boolean =
        Math.abs(baluster.position.x - sized.post.position.x) < reach && Math.abs(baluster.position.z - sized.post.position.z) < reach;
    const low: number = Math.max(baluster.position.y, sized.post.position.y);
    const high: number = Math.min(baluster.position.y + baluster.height, sized.post.position.y + sized.post.height);
    return inPlan && high > low;
}

/**
 * Sets the handrails and the railing posts the way the joiner does: a handrail ends on the face of
 * the post at its end, and every post the handrail meets rises `POST_ABOVE_HANDRAIL` over the
 * handrail top, so the handrail butts into the post side instead of lapping over its top.
 */
export function capRailingPosts(railings: readonly IRailingGeometry[]): void {
    const posts: ISizedPost[] = railings.flatMap((railing: IRailingGeometry) =>
        railing.posts.map((post: IBalusterGeometry) => ({ post, half: railing.postWidth / 2 }))
    );
    for (const railing of railings) {
        const points: IVec3[] = railing.handrail.points;
        if (points.length >= 2) {
            trimEnd(points[0], points[1], posts);
            trimEnd(points[points.length - 1], points[points.length - 2], posts);
        }
    }
    const tops: number[] = posts.map((sized: ISizedPost) =>
        Math.max(...railings.map((railing: IRailingGeometry) => handrailTopOver(railing, sized)))
    );
    posts.forEach((sized: ISizedPost, i: number) => {
        if (Number.isFinite(tops[i])) {
            sized.post.height = tops[i] + POST_ABOVE_HANDRAIL - sized.post.position.y;
        }
    });
    // A post now rising over the handrail takes the place of its own railing's balusters beside it.
    for (const railing of railings) {
        const own: ISizedPost[] = railing.posts.map((post: IBalusterGeometry) => ({ post, half: railing.postWidth / 2 }));
        railing.balusters = railing.balusters.filter(
            (baluster: IBalusterGeometry) => !own.some((sized: ISizedPost) => insidePost(baluster, railing.balusterWidth / 2, sized))
        );
    }
}
